// OffersSection.js - Section 4 : Nos offres pour automatiser votre croissance (sans animations au scroll)
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  MdRocketLaunch,
  MdCheckCircle,
  MdArrowForward,
  MdAutoAwesome,
  MdVerified,
  MdInsights,
  MdTimeline,
  MdEmail,
  MdStorage,
  MdSegment,
  MdRepeat,
  MdAnalytics,
  MdTrendingUp,
  MdSupportAgent,
  MdWorkspacePremium,
  MdRocket,
  MdFlashOn,
  MdDns,
  MdOutlineWhatsapp
} from 'react-icons/md';
import { 
  FaChartLine, 
  FaCrown, 
  FaInfinity,
  FaLinkedin,
  FaWhatsapp,
  FaEnvelope,
  FaRobot,
  FaStar,
  FaTrophy,
  FaBullseye
} from 'react-icons/fa';
import { GiAstronautHelmet, GiGalaxy, GiStarsStack, GiCrystalBall, GiSpiralShell, GiTargetArrows, GiProgression } from 'react-icons/gi';
import './OffersSection.css';

const OffersSection = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const progress = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(progress);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const offers = [
    {
      id: 1,
      icon: <GiTargetArrows />,
      title: "Tunnel complet de prospection",
      description: "Générez des prospects qualifiés grâce à un système structuré sur LinkedIn, email et autres canaux adaptés à votre activité.",
      color: "#3B82F6",
      features: ["LinkedIn automation", "Campagnes email", "Multi-canaux"]
    },
    {
      id: 2,
      icon: <FaWhatsapp />,
      title: "Automatisation WhatsApp, email et CRM",
      description: "Centralisez vos prospects et automatisez vos échanges pour gagner du temps et ne plus laisser aucune opportunité de côté.",
      color: "#25D366",
      features: ["WhatsApp Business API", "Email marketing", "CRM centralisé"]
    },
    {
      id: 3,
      icon: <MdSegment />,
      title: "Segmentation des prospects",
      description: "Classez automatiquement vos prospects selon leur niveau d'intérêt, leur profil ou leur étape dans le parcours d'achat.",
      color: "#8B5CF6",
      features: ["Smart tagging", "Score d'intérêt", "Segmentation dynamique"]
    },
    {
      id: 4,
      icon: <MdRepeat />,
      title: "Relances intelligentes",
      description: "Envoyez le bon message, au bon moment, au bon prospect, sans avoir à le faire manuellement.",
      color: "#10B981",
      features: ["Séquences optimisées", "Timing intelligent", "A/B testing"]
    },
    {
      id: 5,
      icon: <MdAnalytics />,
      title: "Suivi automatique des performances",
      description: "Analysez vos résultats grâce à des tableaux de bord simples : prospects contactés, réponses obtenues, rendez-vous pris et ventes générées.",
      color: "#F59E0B",
      features: ["Dashboard temps réel", "KPIs clés", "Rapports automatiques"]
    },
    {
      id: 6,
      icon: <MdTrendingUp />,
      title: "Optimisation continue",
      description: "Améliorez progressivement vos messages, vos relances et vos scénarios pour obtenir de meilleurs résultats.",
      color: "#06B6D4",
      features: ["A/B testing permanent", "Analyse des données", "Itérations rapides"]
    },
    {
      id: 7,
      icon: <MdSupportAgent />,
      title: "Accompagnement stratégique",
      description: "Bénéficiez d'un suivi personnalisé pour structurer votre acquisition client et prendre les bonnes décisions commerciales.",
      color: "#EC4899",
      features: ["Stratégie sur mesure", "Conseil dédié", "Revues régulières"]
    }
  ];

  return (
    <section className="offers-root" ref={sectionRef}>
      {/* Background orbs animés */}
      <div className="offers-orbs">
        <div className="offers-orb offers-orb-s1" style={{ transform: `translate(${scrollProgress * 80}px, -${scrollProgress * 40}px)` }} />
        <div className="offers-orb offers-orb-s2" style={{ transform: `translate(-${scrollProgress * 60}px, ${scrollProgress * 50}px)` }} />
        <div className="offers-orb offers-orb-s3" style={{ transform: `translate(${scrollProgress * 50}px, ${scrollProgress * 30}px)` }} />
      </div>

      {/* Éléments flottants */}
      <div className="offers-floating">
        <GiAstronautHelmet className="offers-float-icon of1" />
        <GiStarsStack className="offers-float-icon of2" />
        <GiCrystalBall className="offers-float-icon of3" />
        <FaRobot className="offers-float-icon of4" />
        <GiGalaxy className="offers-float-icon of5" />
      </div>

      <div className="offers-container">
        {/* Header */}
        <div className="offers-header">
          <motion.div 
            className="offers-badge-wrapper"
            whileHover={{ scale: 1.03 }}
          >
            <span className="offers-badge">
              <MdWorkspacePremium className="offers-badge-icon" />
              <span>Offres — Machine à clients</span>
              <FaStar className="offers-badge-icon" />
            </span>
            <div className="offers-badge-glow"></div>
          </motion.div>

          <h2 className="offers-title">
            <span className="offers-title-line">
              Nos offres pour <span className="offers-title-gradient">automatiser</span>
            </span>
            <span className="offers-title-line">
              <span className="offers-title-highlight">votre croissance</span>
            </span>
            <div className="offers-title-underline"></div>
          </h2>

          <p className="offers-subtitle">
            Une <span className="offers-highlight">machine à clients complète</span> qui travaille pour vous 
            <br />24h/24, 7j/7.
          </p>
        </div>

        {/* Grille des offres */}
        <div className="offers-grid">
          <div className="offers-grid-header">
            <h3>
              <FaCrown className="offers-header-icon" />
              Ce que <span className="offers-gradient-text">nous mettons en place</span>
            </h3>
            <div className="offers-header-line"></div>
          </div>

          <div className="offers-items-grid">
            {offers.map((offer, index) => (
              <motion.div
                key={offer.id}
                className="offers-card"
                whileHover={{ y: -8, scale: 1.02 }}
              >
                <div className="offers-card-glow" style={{ background: `radial-gradient(circle, ${offer.color}20, transparent 80%)` }} />
                <div className="offers-card-number" style={{ color: `${offer.color}40` }}>
                  {String(offer.id).padStart(2, '0')}
                </div>
                <div className="offers-icon-wrapper" style={{ borderColor: `${offer.color}40`, background: `${offer.color}10` }}>
                  <div className="offers-icon" style={{ color: offer.color }}>
                    {offer.icon}
                  </div>
                </div>
                <h4 className="offers-card-title">{offer.title}</h4>
                <p className="offers-card-description">{offer.description}</p>
                <div className="offers-features">
                  {offer.features.map((feature, idx) => (
                    <span key={idx} className="offers-feature-tag">
                      <MdCheckCircle style={{ color: offer.color }} />
                      {feature}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Résultat */}
        <div className="offers-result">
          <div className="offers-result-card">
            <div className="offers-result-icon">
              <FaTrophy />
            </div>
            <h3>Résultat</h3>
            <p>Vous avez un <span className="offers-gradient-text">système complet</span> qui travaille pour vous et vous aide à <span className="offers-highlight">convertir plus de prospects</span>.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="offers-cta-wrapper">
          <div className="offers-cta-card">
            <div className="offers-cta-glow"></div>
            <div className="offers-cta-pattern"></div>
            
            <div className="offers-cta-content">
              <motion.div 
                className="offers-cta-icon"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <MdRocket />
              </motion.div>
              <h3>Prêt à lancer <span className="offers-gradient-text">votre machine à clients</span> ?</h3>
              <p>Découvrez comment notre système complet peut transformer votre acquisition client.</p>
              
              <div className="offers-cta-features">
                <div className="offers-cta-feature-item">
                  <MdDns />
                  <span>Déploiement rapide</span>
                </div>
                <div className="offers-cta-feature-item">
                  <MdFlashOn />
                  <span>Résultats sous 30 jours</span>
                </div>
                <div className="offers-cta-feature-item">
                  <MdVerified />
                  <span>Satisfaction garantie</span>
                </div>
              </div>
            </div>
            
            <motion.button 
              className="offers-cta-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>Je veux mon système complet</span>
              <MdArrowForward />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OffersSection;